import NextLink from 'next/link';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { Link, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ReactGA from 'react-ga4';
import { UserTypeEnum } from 'enums';
import {
  isUserFromJournal,
  blackNovemberPromotionData,
  isUserEligibleForBlackNovember,
  isUserEligibleForAnniversaryCampaign,
  isAnniversaryActive,
  flowTrialActive,
  userIsAvailableForNewTrialFlow,
} from 'utils';
import PaymentService from 'services/PaymentService';
import Video from 'components/Video';
import ProfileService from 'services/ProfileService';
import { useAuthUser } from 'features/auth/AuthUserContext';

const useStyles = makeStyles((theme) => ({
  videoWrapper: {
    zIndex: 1,
  },
  video: {
    width: 220,
    borderRadius: theme.spacing(1.5),
  },
}));

export default function BannerMyAccount() {
  const { user, profile } = useAuthUser();
  const userFromJournal = isUserFromJournal(profile);
  const userStarter = user.tp_user === UserTypeEnum.TRIAL;
  const classes = useStyles();
  const paymentService = new PaymentService();
  const [paymentInfo, setPaymentInfo] = useState(null);
  // eslint-disable-next-line eqeqeq
  const userResearcherLife = paymentInfo?.data.event == 'r.life-subscription';
  const promotionData = blackNovemberPromotionData();
  const [lastInvoice, setLastInvoice] = useState(null);
  const userNotFromChinaOrJapan = user.country_code !== 'CN' && user.country_code !== 'JP';

  function starterBannerClick() {
    if (
      flowTrialActive &&
      !lastInvoice &&
      userIsAvailableForNewTrialFlow(user) &&
      userNotFromChinaOrJapan
    ) {
      // return '/my-account/billing/checkout/checkout-alt/?billingInterval=month&plan=R&trial=true';
      return '/pricing';
    }

    return `/pricing`;
  }

  async function fetchData() {
    const paymentData = await paymentService.getPaymentInfoById(user.id);
    setPaymentInfo(paymentData);
  }

  function sendBlackNovemberClickTrackingEvents() {
    ReactGA.event('My Account Black November Banner Click');
  }

  function sendUpgradeClickTrackingEvents() {
    ReactGA.event('My Account Banner Stater Upgrade Click');
  }

  function sendResearcherLifeClickTrackingEvents() {
    ReactGA.event('My Account Banner All access Researcher.Life Click');
  }

  function sendFriendsReferralClickTrackingEvents() {
    ReactGA.event('My Account Banner Friends Referral Click');
  }

  function sendAnniversaryClickTrackingEvents() {
    ReactGA.event({
      category: 'my_account',
      action: 'my_account_banner_click_anniversary',
      label: 'Click on the Anniversary banner in my account',
      value: user.id,
    });
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    async function getLastInvoice() {
      const profileService = new ProfileService();
      const { data: lastInvoice } = await profileService.getLastInvoice();
      setLastInvoice(lastInvoice);
    }

    getLastInvoice();
  }, [user]);

  if (isUserEligibleForBlackNovember(user, profile) && promotionData.isActive) {
    return (
      <NextLink href="/blacknovember" passHref={true}>
        <Link onClick={sendBlackNovemberClickTrackingEvents} underline="hover">
          <Image
            src="/images/black-november/black-november-vertical-banner-extend.png"
            alt="Black November Banner"
            loading="lazy"
            width={220}
            height={420}
          />
        </Link>
      </NextLink>
    );
  }

  if (isUserEligibleForAnniversaryCampaign(user, profile) && isAnniversaryActive()) {
    return (
      <NextLink href="/anniversary" passHref={true}>
        <Link onClick={sendAnniversaryClickTrackingEvents} underline="hover">
          <Image
            src="/images/anniversary/my-account-10-anniversary.webp"
            alt="Anniversary vertical image"
            loading="lazy"
            width={220}
            height={420}
          />
        </Link>
      </NextLink>
    );
  }

  if (userStarter && !userFromJournal) {
    return (
      <NextLink href={starterBannerClick()} passHref={true}>
        <Link onClick={sendUpgradeClickTrackingEvents} underline="hover">
          <Image
            src="/images/vertical-banner-starter-upgrade.png"
            alt="vertical banner image"
            loading="lazy"
            width={220}
            height={420}
          />
        </Link>
      </NextLink>
    );
  }

  if (!userStarter && !userFromJournal && !userResearcherLife) {
    return (
      <NextLink
        href="https://researcher.life/all-access?utm_campaign=banner&utm_medium=mtg&utm_source=myaccountpage"
        passHref={true}
      >
        <Link onClick={sendResearcherLifeClickTrackingEvents} underline="hover">
          <Image
            src="/images/vertical-banner-researcher-life.png"
            alt="vertical banner researcher life image"
            loading="lazy"
            width={220}
            height={420}
          />
        </Link>
      </NextLink>
    );
  }

  if (!userStarter && !userFromJournal && userResearcherLife) {
    return (
      <NextLink href="/my-account/invites" passHref={true}>
        <Link onClick={sendFriendsReferralClickTrackingEvents} underline="hover">
          <Box className={classes.videoWrapper}>
            <Video
              className={classes.video}
              sources={[{ src: '/videos/friends-referral-banner.mp4', type: 'video/mp4' }]}
            />
          </Box>
        </Link>
      </NextLink>
    );
  }
}
